import type { ReactNode } from 'react';
import { formatDate, formatMoney, formatNumber, t } from '@aquabox/shared';
import {
  usePersonnelPerformance,
  type PerfFleetAverage,
  type PerfWindowKey,
  type PerfWindowStats,
  type PersonnelPerformance,
  type PersonnelPerformanceRow,
} from '../api/personnel-performance';

/**
 * Personel performansı (UX-C) — `GET /api/reports/personnel-performance`.
 *
 * İki parça: kişi detayındaki `PersonPerformance` (7 ve 30 gün, kişi ve filo ortalaması yan yana,
 * kaynak kırılımı, sayım uyuşmazlığı kanıt listesi) ve Performans sekmesindeki `PerformanceOverview`
 * (herkesin tek tablosu).
 *
 * KURAL: para ve ortalama panelde HESAPLANMAZ (CLAUDE.md kural 4) — sunucudan string gelir,
 * panel yalnız biçimlendirir. TEK SKOR YOKTUR; yeşil/kırmızı yalnız uyuşmazlık satırındadır ve
 * hemen yanında hangi seferden doğduğu listelenir.
 */

const WINDOWS: PerfWindowKey[] = ['d7', 'd30'];
const WINDOW_LABEL: Record<PerfWindowKey, Parameters<typeof t>[0]> = {
  d7: 'personnel.perfWindow7',
  d30: 'personnel.perfWindow30',
};
const SOURCE_KEY = {
  MANUAL: 'rep.ownerSrc.MANUAL',
  GETIR_SU: 'rep.ownerSrc.GETIR_SU',
  HAYAT_PANEL: 'rep.ownerSrc.HAYAT_PANEL',
  ABANT_PANEL: 'rep.ownerSrc.ABANT_PANEL',
} as const;

/** Ortalama tam sayı olmayabilir (1,5 teslimat); en fazla bir ondalık. */
function avg(n: number): string {
  return n.toLocaleString('tr-TR', { maximumFractionDigits: 1 });
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

const th: React.CSSProperties = {
  textAlign: 'right',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  padding: '6px 8px',
};
const td: React.CSSProperties = {
  textAlign: 'right',
  fontSize: 13.5,
  padding: '6px 8px',
  fontVariantNumeric: 'tabular-nums',
  borderTop: '1px solid var(--border)',
};
const tdLabel: React.CSSProperties = { ...td, textAlign: 'left', color: 'var(--text-secondary)' };

function PerfError() {
  return (
    <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
      {t('personnel.perfLoadError')}
    </p>
  );
}

/** Kişi detayı "Performans" sekmesi. */
export function PersonPerformance({ driverId }: { driverId: string }) {
  const q = usePersonnelPerformance();
  if (q.isError) return <PerfError />;
  // Beklenmeyen/boş gövde (ör. uç henüz yanıt vermedi) ekranı düşürmesin.
  if (!q.data?.drivers) return <p style={{ fontSize: 13 }}>{t('common.loading')}</p>;
  const row = q.data.drivers.find((r) => r.driver.id === driverId);
  if (!row) return <p style={{ fontSize: 13 }}>{t('personnel.perfNoTrips')}</p>;
  return <PersonPerformanceBody row={row} data={q.data} />;
}

function PersonPerformanceBody({
  row,
  data,
}: {
  row: PersonnelPerformanceRow;
  data: PersonnelPerformance;
}) {
  const fleet = data.fleetAverage;
  type Metric = {
    label: string;
    mine: (s: PerfWindowStats) => ReactNode;
    fleet: (f: PerfFleetAverage) => ReactNode;
  };
  const metrics: Metric[] = [
    {
      label: t('personnel.perfTrips'),
      mine: (s) => s.tripCount,
      fleet: (f) => avg(f.tripCount),
    },
    {
      label: t('personnel.perfDeliveries'),
      mine: (s) => s.deliveredCount,
      fleet: (f) => avg(f.deliveredCount),
    },
    { label: t('personnel.perfJugs'), mine: (s) => s.jugs, fleet: (f) => avg(f.jugs) },
    {
      label: t('pay.cash'),
      mine: (s) => formatMoney(s.collected.cash),
      fleet: (f) => formatMoney(f.collected.cash),
    },
    {
      label: t('pay.card'),
      mine: (s) => formatMoney(s.collected.card),
      fleet: (f) => formatMoney(f.collected.card),
    },
    {
      label: t('pay.online'),
      mine: (s) => formatMoney(s.collected.online),
      fleet: (f) => formatMoney(f.collected.online),
    },
    {
      label: t('pay.credit'),
      mine: (s) => formatMoney(s.collected.credit),
      fleet: (f) => formatMoney(f.collected.credit),
    },
    {
      label: t('personnel.perfDeposit'),
      mine: (s) => formatMoney(s.depositCollected),
      fleet: () => '—',
    },
  ];

  return (
    <div
      data-testid="person-performance"
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, maxWidth: 720 }}>
        {t('personnel.perfIntro')}
      </p>

      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 760 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }} />
            {WINDOWS.map((w) => (
              <th key={w} colSpan={2} style={{ ...th, textAlign: 'center' }}>
                {t(WINDOW_LABEL[w])}
              </th>
            ))}
          </tr>
          <tr>
            <th style={{ ...th, textAlign: 'left' }} />
            {WINDOWS.flatMap((w) => [
              <th key={`${w}-me`} style={th}>
                {t('personnel.perfThisPerson')}
              </th>,
              <th
                key={`${w}-fleet`}
                style={th}
                title={t('personnel.perfFleetNote', { n: fleet[w].driverCount })}
              >
                {t('personnel.perfFleet')}
              </th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.label}>
              <td style={tdLabel}>{m.label}</td>
              {WINDOWS.flatMap((w) => [
                <td key={`${w}-me`} style={{ ...td, fontWeight: 600 }}>
                  {m.mine(row[w])}
                </td>,
                <td key={`${w}-fleet`} style={td}>
                  {m.fleet(fleet[w])}
                </td>,
              ])}
            </tr>
          ))}
          <tr>
            <td style={tdLabel}>{t('personnel.perfFloat')}</td>
            <td colSpan={4} style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
              {formatMoney(row.floatBalance)}
            </td>
          </tr>
        </tbody>
      </table>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <strong style={{ fontSize: 13 }}>{t('personnel.perfBySource')}</strong>
        <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 760 }}>
          <tbody>
            {(Object.keys(SOURCE_KEY) as (keyof typeof SOURCE_KEY)[]).map((src) => (
              <tr key={src}>
                <td style={tdLabel}>{t(SOURCE_KEY[src])}</td>
                {WINDOWS.map((w) => {
                  const s = row[w].bySource.find((x) => x.source === src);
                  return (
                    <td key={w} style={{ ...td, textAlign: 'left' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {t(WINDOW_LABEL[w])}:{' '}
                      </span>
                      {t('personnel.perfSourceLine', {
                        n: s?.deliveredCount ?? 0,
                        jugs: s?.jugs ?? 0,
                        tutar: formatMoney(s?.amount ?? '0'),
                      })}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>{t('personnel.perfMismatch')}</strong>
        {WINDOWS.map((w) => (
          <MismatchBlock key={w} windowKey={w} stats={row[w]} />
        ))}
      </section>
    </div>
  );
}

function MismatchBlock({ windowKey, stats }: { windowKey: PerfWindowKey; stats: PerfWindowStats }) {
  const m = stats.mismatch;
  const none = m.closedTrips === 0;
  const bad = m.mismatchTripCount > 0;
  const text = none
    ? t('personnel.perfMismatchNone')
    : bad
      ? t('personnel.perfMismatchBad', { k: m.mismatchTripCount, n: m.closedTrips })
      : t('personnel.perfMismatchOk', { n: m.closedTrips });
  return (
    <div data-testid={`mismatch-${windowKey}`} style={{ fontSize: 13.5 }}>
      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
        {t(WINDOW_LABEL[windowKey])}:{' '}
      </span>
      <span
        style={{
          fontWeight: 600,
          color: none ? 'var(--text-secondary)' : bad ? 'var(--danger)' : 'var(--success)',
        }}
      >
        {text}
      </span>
      {bad ? (
        <details style={{ marginTop: 4 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12.5 }}>
            {t('personnel.perfMismatchShow')}
          </summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
            {m.trips.map((tr) => (
              <li key={tr.tripId}>
                {t('personnel.perfMismatchRow', {
                  gun: formatDate(tr.day),
                  plaka: tr.plate,
                  dolu: signed(tr.diffFull),
                  bos: signed(tr.diffEmpty),
                })}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            {t('personnel.perfMismatchRowHint')}
          </p>
        </details>
      ) : null}
    </div>
  );
}

/** Performans sekmesi: herkes tek tabloda; satıra tıklayınca kişi detayı (Kişiler sekmesi) açılır. */
export function PerformanceOverview({ onOpen }: { onOpen?: (driverId: string) => void }) {
  const q = usePersonnelPerformance();
  if (q.isError) {
    return (
      <div style={{ padding: '0 32px' }}>
        <PerfError />
      </div>
    );
  }
  const data = q.data;
  if (!data?.drivers || !data.fleetAverage) return null;
  return (
    <section
      className="card"
      data-testid="performance-overview"
      style={{ margin: '0 32px', padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <strong style={{ fontSize: 15 }}>{t('personnel.perfOverviewTitle')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, maxWidth: 720 }}>
        {t('personnel.perfIntro')}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>{t('personnel.perfColPerson')}</th>
              {WINDOWS.flatMap((w) => [
                <th key={`${w}-d`} style={th}>
                  {t(WINDOW_LABEL[w])} · {t('personnel.perfDeliveries')}
                </th>,
                <th key={`${w}-j`} style={th}>
                  {t('personnel.perfJugs')}
                </th>,
                <th key={`${w}-c`} style={th}>
                  {t('pay.cash')}
                </th>,
                <th key={`${w}-m`} style={th}>
                  {t('personnel.perfColMismatch')}
                </th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {data.drivers.map((r) => (
              <tr
                key={r.driver.id}
                onClick={onOpen ? () => onOpen(r.driver.id) : undefined}
                style={{ cursor: onOpen ? 'pointer' : 'default' }}
              >
                <td style={{ ...tdLabel, color: 'inherit', fontWeight: 600 }}>
                  {r.driver.fullName}
                </td>
                {WINDOWS.flatMap((w) => [
                  <td key={`${w}-d`} style={td}>
                    {formatNumber(r[w].deliveredCount)}
                  </td>,
                  <td key={`${w}-j`} style={td}>
                    {formatNumber(r[w].jugs)}
                  </td>,
                  <td key={`${w}-c`} style={td}>
                    {formatMoney(r[w].collected.cash)}
                  </td>,
                  <td
                    key={`${w}-m`}
                    style={{
                      ...td,
                      color: r[w].mismatch.mismatchTripCount > 0 ? 'var(--danger)' : undefined,
                    }}
                  >
                    {r[w].mismatch.mismatchTripCount}
                  </td>,
                ])}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdLabel, fontWeight: 600 }}>{t('personnel.perfFleet')}</td>
              {WINDOWS.flatMap((w) => [
                <td key={`${w}-d`} style={td}>
                  {avg(data.fleetAverage[w].deliveredCount)}
                </td>,
                <td key={`${w}-j`} style={td}>
                  {avg(data.fleetAverage[w].jugs)}
                </td>,
                <td key={`${w}-c`} style={td}>
                  {formatMoney(data.fleetAverage[w].collected.cash)}
                </td>,
                <td key={`${w}-m`} style={td}>
                  {avg(data.fleetAverage[w].mismatchTripCount)}
                </td>,
              ])}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
